import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import {
  oiggService,
  type AdverseCheckHistoryListItem,
} from "@services/OIGGService";
import {
  getFacilityHierarchyId,
  getSelectedFacilityFromSession,
} from "@utils/facilityUtils";

export type ExclusionCheckHistoryListProps = {
  /** Facility that owns the history; falls back to session selection. */
  facilityData?: unknown;
  /** When false, skip loading (e.g. tab not visible). Defaults true. */
  active?: boolean;
  /** Bump after a new run is persisted so the list refetches (AB#10338). */
  refreshKey?: number;
};

function resolveFacilityId(facilityData?: unknown): number {
  const facility = facilityData || getSelectedFacilityFromSession() || null;
  if (!facility) return 0;
  const rawId = getFacilityHierarchyId(
    facility as Parameters<typeof getFacilityHierarchyId>[0],
  );
  const facilityId = Number(rawId ?? 0);
  return Number.isFinite(facilityId) && facilityId > 0 ? facilityId : 0;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

function formatRunType(runType: string): string {
  const t = (runType || "").toLowerCase();
  if (t === "bulk") return "Bulk";
  if (t === "single") return "Single";
  return runType || "—";
}

/**
 * History tab run list (#10121): paginated past checks with per-row Download XLS.
 */
export default function ExclusionCheckHistoryList({
  facilityData,
  active = true,
  refreshKey = 0,
}: ExclusionCheckHistoryListProps) {
  const facilityId = useMemo(
    () => resolveFacilityId(facilityData),
    [facilityData],
  );
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState<AdverseCheckHistoryListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage) || 1);
  const startIndex = total === 0 ? 0 : (page - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, total);

  const loadPage = useCallback(async () => {
    if (!active || !facilityId) {
      setItems([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const resp = await oiggService.listAdverseCheckHistory(
        facilityId,
        page,
        rowsPerPage,
      );
      setItems(resp.data?.items ?? []);
      setTotal(Number(resp.data?.total ?? 0));
    } catch (err) {
      console.error("Failed to load exclusion check history:", err);
      toast.error("Could not load run history.");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
    // refreshKey intentionally in deps so persist bumps re-fetch the list (AB#10338).
  }, [active, facilityId, page, rowsPerPage, refreshKey]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleDownload = async (row: AdverseCheckHistoryListItem) => {
    setDownloadingId(row.batch_id);
    try {
      const { blob, filename } =
        await oiggService.downloadAdverseCheckHistoryRun(row.batch_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download history run:", err);
      toast.error("Download failed.");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Box data-testid="exclusion-check-history-list">
      <Typography
        sx={{
          fontFamily: "Rethink Sans, sans-serif",
          fontWeight: 600,
          fontSize: "16px",
          color: "#0f172a",
          mb: "4px",
        }}
      >
        Run history
      </Typography>
      <Typography
        sx={{
          fontFamily: "Rethink Sans, sans-serif",
          fontWeight: 400,
          fontSize: "13px",
          color: "#64748b",
          mb: 2,
        }}
      >
        Every past check. Download any run&apos;s audit copy.
      </Typography>

      {!facilityId ? (
        <Typography
          data-testid="history-list-no-facility"
          sx={{
            fontFamily: "Rethink Sans, sans-serif",
            fontSize: "13px",
            color: "#64748b",
          }}
        >
          Select a facility to view run history.
        </Typography>
      ) : (
        <>
          <TableContainer
            sx={{
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <Table size="small" aria-label="Run history">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  {[
                    "Run name",
                    "Type",
                    "When",
                    "Records",
                    "Operator",
                    "Action",
                  ].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        fontFamily: "Rethink Sans, sans-serif",
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "#475569",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && items.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      sx={{
                        fontFamily: "Rethink Sans, sans-serif",
                        fontSize: "13px",
                        color: "#64748b",
                        py: 3,
                      }}
                    >
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      data-testid="history-list-empty"
                      sx={{
                        fontFamily: "Rethink Sans, sans-serif",
                        fontSize: "13px",
                        color: "#64748b",
                        py: 3,
                      }}
                    >
                      No past runs for this facility yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((row) => (
                    <TableRow
                      key={row.batch_id}
                      data-testid={`history-row-${row.batch_id}`}
                      hover={false}
                      sx={{
                        cursor: "default",
                        "& td": {
                          fontFamily: "Rethink Sans, sans-serif",
                          fontSize: "13px",
                          color: "#0f172a",
                          borderBottom: "1px solid #f1f5f9",
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>
                        {row.run_name}
                      </TableCell>
                      <TableCell>{formatRunType(row.run_type)}</TableCell>
                      <TableCell>{formatWhen(row.queried_at)}</TableCell>
                      <TableCell>{row.record_count}</TableCell>
                      <TableCell>{row.operator}</TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          size="small"
                          disabled={downloadingId === row.batch_id}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDownload(row);
                          }}
                          sx={{
                            textTransform: "none",
                            fontFamily: "Rethink Sans, sans-serif",
                            fontSize: "12px",
                            fontWeight: 500,
                            color: "#0e7490",
                            minWidth: 0,
                            px: 1,
                            "&:hover": {
                              backgroundColor: "#ecfeff",
                            },
                          }}
                        >
                          ↓ Download XLS
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {total > 0 && (
            <Box
              data-testid="history-list-pagination"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                mt: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Rethink Sans, sans-serif",
                  fontSize: 12,
                  color: "#065b70",
                }}
              >
                Showing {total === 0 ? 0 : startIndex + 1}-{endIndex} of {total}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  sx={{
                    fontFamily: "Rethink Sans, sans-serif",
                    fontSize: 12,
                    color: "#065b70",
                  }}
                >
                  Rows per page
                </Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(event) => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                  sx={{
                    minWidth: 72,
                    width: 72,
                    height: 28,
                    "& .MuiSelect-select": {
                      padding: "4px 8px",
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                </Select>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  size="small"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontSize: 12,
                      height: 28,
                      minWidth: 28,
                      padding: "0 6px",
                    },
                    "& .MuiPaginationItem-root.Mui-selected": {
                      backgroundColor: "#0a829d",
                      color: "#ffffff",
                    },
                    "& .MuiPaginationItem-root.Mui-selected:hover": {
                      backgroundColor: "#0a829d",
                    },
                    "& .MuiPaginationItem-previousNext": {
                      color: "#9ca3af",
                    },
                  }}
                />
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
